import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {
    ArrayModel,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    DopModel,
    EventService,
    IMaterialPickupResult,
    MATERIAL_SOURCE_CODE,
    MaterialIdentifierModel,
    MaterialModel,
    MaterialPickupComponent,
    MaterialService,
    MaterialUtilitiesService,
    ModalClass,
    NotificationDataInstanceToken,
    NotificationDataInterface,
    NotificationModel,
    ParticipantModel,
    Region,
    UiResponsiveService,
    WindowService,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import {
    faExclamationTriangle,
    faExternalLinkSquareAlt,
    faInfoCircle,
    faSearch,
    faTaxi,
    faWrench,
} from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { CarouselComponent } from 'ngx-bootstrap/carousel';
import { BsModalService } from 'ngx-bootstrap/modal';
import { of, Subject } from 'rxjs';
import { catchError, mergeMap, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'pace-details-carousel',
    templateUrl: './details-carousel.component.html',
    styleUrls: ['./details-carousel.component.scss'],
    standalone: false,
})
export class DetailsCarouselComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {
    @Input()
    public notification: NotificationModel;
    private window: Window;
    public slides: Array<Array<Array<ParticipantModel | MaterialModel>>> = [];
    public showParticipantList = false;
    private listMaterial: ArrayModel<MaterialModel> = new ArrayModel('MaterialList', []);
    public strings: {
        title: string;
        count: string;
    };
    public faInfoCircle = faInfoCircle;
    public faExternalLinkSquareAlt = faExternalLinkSquareAlt;
    public sparePartCatalogMatLink: string;
    public faTaxi = faTaxi;
    public faExclamationTriangle = faExclamationTriangle;
    public faSearch = faSearch;
    public faWrench = faWrench;

    @ViewChild('carousel')
    public carousel: CarouselComponent;

    public numberOfSlideColumns: number;

    private readonly detailDestroy = new Subject<void>();

    private readonly COLUMNS_FOR_REGION: Record<Region, number> = {
        Unknown: 1,
        SmallRegion: 2,
        MediumRegion: 2,
        LargeRegion: 3,
        DesktopRegion: 4,
        LargeDesktopRegion: 4,
    };
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly modalService: BsModalService,
        private readonly translate: TranslateService,
        private readonly windowService: WindowService,
        private readonly materialService: MaterialService,
        private readonly materialUtilitiesService: MaterialUtilitiesService,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        private readonly eventService: EventService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly changeDetectionRef: ChangeDetectorRef,
        private readonly uiResponsiveService: UiResponsiveService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {
        this.window = this.windowService.nativeWindow;
    }

    public ngOnInit(): void {
        this.uiResponsiveService.currentRegion$.pipe(takeUntil(this.detailDestroy)).subscribe((region) => {
            this.renderCarouselForRegion(region);
        });

        this.eventService.refreshMaterialReturnCount$
            .pipe(
                tap(() => this.changeDetectionRef.detectChanges()),
                takeUntil(this.detailDestroy),
            )
            .subscribe();
    }

    public ngAfterViewInit(): void {
        // this event is triggered after notifications are updated, hence participants List, if available, will also be refreshed
        this.eventService.materialsUpdated$.pipe(takeUntil(this.detailDestroy)).subscribe(() => {
            if (!this.notification) {
                return;
            }
            // get the latest notification model with updated properties
            const notification = this.notificationService.getNotificationByIdentifier(this.notification.identifier);
            if (!notification) {
                throw new Error(`Could not re-load notification for identifier ${this.notification.identifier}`);
            }
            this.notification = notification;
            this.initData();
        });
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj && changesObj.notification.currentValue) {
            this.notification = <NotificationModel>changesObj.notification.currentValue;
            this.initData();
        }
    }

    public ngOnDestroy(): void {
        this.detailDestroy.next();
        this.detailDestroy.complete();
    }

    public openEmail(email: string): void {
        this.window.location.href = 'mailto:' + email;
    }

    public initData(): void {
        this.slides = []; // reset the slides before creating with new data
        this.showParticipantList = this.configService.showParticipantList(this.notification.taskGroup);
        let data: Array<ParticipantModel | MaterialModel> = [];
        if (this.showParticipantList) {
            data = this.notification.participantSet;
        } else {
            this.listMaterial.value = this.materialService.getAllMaterialsForNotification(this.notification.identifier);
            this.listMaterial.value.map((materialModel: MaterialModel) => {
                // display only those materials in details screen which are ordered for the notification and are active.
                // Materials created as a result of split are not displayed here.
                if (
                    materialModel.materialSource.sourceCode === MATERIAL_SOURCE_CODE.ORDERED &&
                    materialModel.showOnNotificationDetail
                ) {
                    data.push(materialModel);
                }
            });
        }
        if (data.length && this.numberOfSlideColumns) {
            const participantListIndex = 2;
            const maxNoOfItemsPerColumn = this.showParticipantList ? participantListIndex : 1;
            const numberOfSlides = Math.ceil(data.length / this.numberOfSlideColumns);

            const slides: Array<Array<Array<ParticipantModel | MaterialModel>>> = [];
            const slide: Array<Array<ParticipantModel | MaterialModel>> = [];
            for (let slideIndex = 0; slideIndex < numberOfSlides; slideIndex++) {
                slide.length = 0;
                const column: Array<ParticipantModel | MaterialModel> = [];
                const startIndex = slideIndex * this.numberOfSlideColumns * maxNoOfItemsPerColumn;
                const endIndex = Math.min(
                    (slideIndex + 1) * this.numberOfSlideColumns * maxNoOfItemsPerColumn,
                    data.length,
                );
                for (let dataIndex = startIndex; dataIndex < endIndex; dataIndex++) {
                    if (dataIndex > startIndex && dataIndex % maxNoOfItemsPerColumn === 0) {
                        slide.push([...column]);
                        column.length = 0;
                    }
                    column.push(data[dataIndex]);
                }

                if (column.length > 0) {
                    slide.push([...column]);
                }
                if (slide.length > 0) {
                    slides.push([...slide]);
                }
            }
            this.slides = slides;
        }
        this.strings = {
            title: this.showParticipantList
                ? <string>this.translate.instant('NOTIFICATION_DETAILS_PARTICIPANTLIST_TITLE')
                : <string>this.translate.instant('NOTIFICATION_DETAILS_MATERIAL_TITLE'),
            count: this.showParticipantList ? `(${this.notification.participantSet.length})` : `(${data.length})`,
        };
        const mainEquipment = this.notification.getMainEquipment();
        if (mainEquipment) {
            this.sparePartCatalogMatLink = this.materialUtilitiesService.getSparePartCatalogMatLink(
                mainEquipment.matnr.value,
            );
        }

        if (this.carousel) {
            // reset the active slide index to the first slide when a new notification is loaded,
            // or columns are newly sorted for a different screen size
            this.carousel.activeSlide = 0;
        }
    }

    public openPickupModal(item: MaterialModel): void {
        const dopModel = this.notification.dopSet.find((dop: DopModel) => {
            return (
                dop.poNum.value === parseInt(item.batchNumber.value, 10) ||
                dop.poNum.value === parseInt(item.awbDelivery, 10)
            );
        });

        const modalInstance = this.modalService.show(MaterialPickupComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                id: this.notification.identifier,
                material: item,
                dopModel: dopModel,
                displayNotReceived: this.clientManager.isNgmP58(),
            },
            ariaLabelledBy: 'material-pickup-modal-title',
        });

        modalInstance.content?.modalResult
            .pipe(
                mergeMap((result: IMaterialPickupResult) => {
                    if (result && result.materialModel && result.pickedQuantity) {
                        const index = this.listMaterial.value.findIndex(
                            (materialModel) =>
                                MaterialIdentifierModel.compare(
                                    materialModel.materialIdentifier,
                                    item.materialIdentifier,
                                ) && materialModel.materialKey === item.materialKey,
                        );
                        return this.materialService.setStatus(
                            this.listMaterial,
                            index,
                            result.pickedQuantity,
                            result.statusCode,
                        );
                    }
                    return of(undefined);
                }),
                tap(() => {
                    if (this.clientManager.isNgmP58()) {
                        this.eventService.triggerMaterialConsumptionOrNotReceivedChanged(this.listMaterial.value);
                    }
                }),
                catchError((error) => {
                    this.log.error(
                        'Error in DetailsCarouselComponent -> openPickupModal -> materialService.setStatus : ',
                        error,
                    );
                    return of(undefined);
                }),
            )
            .subscribe(() => this.changeDetectionRef.detectChanges());
    }

    public getMaterialPickedUpQuantity(material: MaterialModel): number | undefined {
        return MaterialService.pickedUpQuantityFromLocalStorage(material);
    }

    public swipe(action = ConfigConstants.SWIPE_ACTION.RIGHT): void {
        if (this.carousel.activeSlide === undefined) {
            this.carousel.activeSlide = 0;
        }
        if (action === ConfigConstants.SWIPE_ACTION.RIGHT) {
            // if you swipe right show the previous slide
            this.carousel.previousSlide();
        } else if (action === ConfigConstants.SWIPE_ACTION.LEFT) {
            // if you swipe left show the next slide
            this.carousel.nextSlide();
        }
    }

    private renderCarouselForRegion(region: Region): void {
        const newNumberOfSlides = this.COLUMNS_FOR_REGION[region];
        if (newNumberOfSlides !== this.numberOfSlideColumns || this.numberOfSlideColumns === undefined) {
            this.numberOfSlideColumns = newNumberOfSlides;
            this.initData();
        }
    }
}
